import { useIntl } from 'react-intl'

import type { OrchestratedQuote } from '@b-stock/fe-core/bstock-next/queries'
import {
  CurrencyStringCents,
  CurrencyStringDollars,
  centsToDollars,
} from '@b-stock/fe-core/bstock-react'

import type { CardOrderItem } from '@/buyer-portal/components/CardOrder'
import {
  sellerCollectsCopyId,
  type SaleKind,
} from '@/buyer-portal/components/features/listing/actions/auction/BidFormProvider/BidConfirmationModal/sellerCollectsBidCopy'
import type { FeeModel } from '@/buyer-portal/helpers/sellerCollectsFeeModel'

/**
 * One `Estimated Total Payment` row, with the two payees inside it.
 *
 * BCD-4 asks for a single total and no breakdown above the fold, so the payee
 * split moves one level down rather than away: a Seller Collects buyer owes
 * two different parties and still needs to see which is which, but the first
 * thing the modal states is what the bid commits them to in total.
 *
 * The seller group carries the bid and, where the seller bills freight, the
 * shipping; the B-Stock group carries only the fee. Shipping is included only
 * where B-Stock actually quotes it (`BOOKED_THROUGH_BSTOCK`); every other
 * arrangement is settled off-platform and no amount is asserted.
 */
export const useSellerCollectsPaymentInfo = ({
  bidAmountDollars,
  bstockFeeCents,
  currency,
  feeModel,
  paymentDeadlineDays,
  saleKind = 'bid',
  sellerName,
  sellerShippingCents,
}: {
  bidAmountDollars: number
  bstockFeeCents: number
  currency: string
  feeModel: FeeModel
  /** Undefined where the storefront asserts no deadline. */
  paymentDeadlineDays?: number
  /** Buy Now has no max bid and no winning, so those labels change. */
  saleKind?: SaleKind
  sellerName: string
  /** Only set where the seller bills freight with the goods. */
  sellerShippingCents?: number
}): CardOrderItem[] => {
  const { formatMessage } = useIntl()
  const id = (key: string) => sellerCollectsCopyId(`total.${key}`, saleKind)

  const sellerChildren: CardOrderItem[] = [
    {
      title: formatMessage({ id: id('maxBid') }),
      value: (
        <CurrencyStringDollars value={bidAmountDollars} currency={currency} />
      ),
    },
  ]

  if (sellerShippingCents !== undefined) {
    sellerChildren.push({
      title: formatMessage({ id: id('shipping') }),
      value: (
        <CurrencyStringCents value={sellerShippingCents} currency={currency} />
      ),
    })
  }

  const sellerTotalDollars =
    bidAmountDollars + centsToDollars(sellerShippingCents ?? 0)

  /** BCD-4's single headline row; what sits under it is the payee split. */
  const estimatedTotal = (totalDollars: number): CardOrderItem => ({
    title: formatMessage({ id: id('estimatedTotal') }),
    value: <CurrencyStringDollars value={totalDollars} currency={currency} />,
  })

  const sellerGroup: CardOrderItem = {
    title: formatMessage({ id: id('toSeller') }, { sellerName }),
    value: (
      <CurrencyStringDollars value={sellerTotalDollars} currency={currency} />
    ),
    description:
      paymentDeadlineDays === undefined
        ? undefined
        : formatMessage({ id: id('deadline') }, { days: paymentDeadlineDays }),
    children: sellerChildren,
  }

  // A fee-free storefront owes one party, so the payee level would be a
  // single group wrapping a single group. It keeps the seller's own rows.
  if (feeModel !== 'feeBearing') {
    return [
      {
        ...estimatedTotal(sellerTotalDollars),
        children: sellerChildren,
      },
    ]
  }

  return [
    {
      ...estimatedTotal(sellerTotalDollars + centsToDollars(bstockFeeCents)),
      children: [
        sellerGroup,
        {
          title: formatMessage({ id: id('toBStock') }),
          value: (
            <CurrencyStringCents value={bstockFeeCents} currency={currency} />
          ),
          description: formatMessage({ id: id('toBStockDescription') }),
          children: [
            {
              title: formatMessage({ id: id('bstockFee') }),
              value: (
                <CurrencyStringCents
                  value={bstockFeeCents}
                  currency={currency}
                />
              ),
            },
          ],
        },
      ],
    },
  ]
}

/**
 * The shipping amount the seller bills with the goods, or undefined where
 * B-Stock has no quoted amount for this storefront.
 *
 * A quote carrying no `totalPrice` is no amount at all, not a free shipment —
 * defaulting it to 0 would assert a $0.00 freight line the storefront never
 * quoted. A genuine zero-rate quote still reads as 0 and shows its row.
 */
export const getSellerBilledShippingCents = (
  quotesShipping: boolean,
  selectedQuote: OrchestratedQuote | null | undefined
): number | undefined =>
  quotesShipping ? selectedQuote?.totalPrice : undefined
